import React, { createContext, useContext, useState, useEffect } from 'react';
import { trackAddToCart, trackCheckoutStarted } from '../utils/tracking';
import { trackAddToCart as analyticsAddToCart } from '../lib/analytics';

const CartContext = createContext();

// Known discount codes configured in Shopify Admin (display % must match Shopify rule)
export const DISCOUNT_CODES = {
  COMBO10: { code: 'COMBO10', percent: 10, label: 'Bundle Offer' },
};

export const useCart = () => {
  const context = useContext(CartContext);
  if (!context) {
    throw new Error('useCart must be used within a CartProvider');
  }
  return context;
};

// Smooth, branded hand-off shown while we create the checkout + redirect
const CheckoutRedirectOverlay = () => (
  <div className="fixed inset-0 z-[100] bg-white/92 backdrop-blur-sm flex flex-col items-center justify-center px-6 text-center" data-testid="checkout-redirect-overlay">
    <div className="w-14 h-14 border-4 border-amber-200 border-t-amber-500 rounded-full animate-spin" />
    <p className="mt-5 text-lg font-semibold text-slate-800">Taking you to secure checkout…</p>
    <p className="mt-1 text-sm text-slate-500 flex items-center gap-1">
      <span>🔒</span> 100% secure payment · please don't close this window
    </p>
  </div>
);

export const CartProvider = ({ children }) => {
  const [cartItems, setCartItems] = useState(() => {
    const saved = localStorage.getItem('cart');
    return saved ? JSON.parse(saved) : [];
  });
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [checkoutLoading, setCheckoutLoading] = useState(false);
  const [appliedDiscountCode, setAppliedDiscountCode] = useState(() => {
    return localStorage.getItem('discount_code') || null;
  });

  useEffect(() => {
    localStorage.setItem('cart', JSON.stringify(cartItems));
    // Drop any applied discount once the cart is emptied
    if (cartItems.length === 0 && appliedDiscountCode) {
      setAppliedDiscountCode(null);
    }
  }, [cartItems]);

  useEffect(() => {
    if (appliedDiscountCode) {
      localStorage.setItem('discount_code', appliedDiscountCode);
    } else {
      localStorage.removeItem('discount_code');
    }
  }, [appliedDiscountCode]);

  const applyDiscountCode = (code) => {
    const normalized = (code || '').toUpperCase();
    if (DISCOUNT_CODES[normalized]) {
      setAppliedDiscountCode(normalized);
    }
  };

  const removeDiscountCode = () => setAppliedDiscountCode(null);

  const addToCart = (product, quantity = 1) => {
    // Track add to cart event
    trackAddToCart(product, quantity);
    try { analyticsAddToCart(product.id, product.name); } catch (e) { /* ignore */ }
    
    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) =>
          item.id === product.id
            ? { ...item, quantity: item.quantity + quantity }
            : item
        );
      }
      return [...prev, { ...product, quantity }];
    });
    setIsCartOpen(true);
  };

  const removeFromCart = (productId) => {
    setCartItems((prev) => prev.filter((item) => item.id !== productId));
  };

  const updateQuantity = (productId, quantity) => {
    if (quantity < 1) {
      removeFromCart(productId);
      return;
    }
    setCartItems((prev) =>
      prev.map((item) =>
        item.id === productId ? { ...item, quantity } : item
      )
    );
  };

  const clearCart = () => {
    setCartItems([]);
    setAppliedDiscountCode(null);
  };

  const cartTotal = cartItems.reduce(
    (total, item) => total + item.price * item.quantity,
    0
  );

  const cartCount = cartItems.reduce((count, item) => count + item.quantity, 0);

  // Discount derived from the applied code (display estimate; Shopify applies real value at checkout)
  const discountPercent = appliedDiscountCode
    ? DISCOUNT_CODES[appliedDiscountCode]?.percent || 0
    : 0;
  const discountAmount = Math.round((cartTotal * discountPercent) / 100);
  const discountedTotal = cartTotal - discountAmount;

  // Own on-site checkout — route to /checkout (no more Shopify redirect)
  const proceedToCheckout = () => {
    if (cartItems.length === 0) return;
    trackCheckoutStarted(cartItems, cartTotal);
    setIsCartOpen(false);
    window.location.href = '/checkout';
  };

  // Buy Now - add the item then go straight to our checkout
  const buyNow = (product, quantity = 1) => {
    if (!product || !product.variantId && !product.id) {
      alert('Product not found. Please try again.');
      return;
    }
    setCartItems((prev) => {
      const existing = prev.find((item) => item.id === product.id);
      if (existing) {
        return prev.map((item) => item.id === product.id ? { ...item, quantity: item.quantity + quantity } : item);
      }
      return [...prev, { ...product, quantity }];
    });
    trackAddToCart(product, quantity);
    window.location.href = '/checkout';
  };

  const value = {
    cartItems,
    addToCart,
    removeFromCart,
    updateQuantity,
    clearCart,
    cartTotal,
    cartCount,
    isCartOpen,
    setIsCartOpen,
    proceedToCheckout,
    buyNow,
    checkoutLoading,
    appliedDiscountCode,
    applyDiscountCode,
    removeDiscountCode,
    discountPercent,
    discountAmount,
    discountedTotal,
  };

  return (
    <CartContext.Provider value={value}>
      {children}
      {checkoutLoading && <CheckoutRedirectOverlay />}
    </CartContext.Provider>
  );
};
